import { Zap, Wrench, Truck, Disc3, RotateCcw, Car, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/constants/testIds";
import { SERVICE_IMAGES, telHref } from "@/constants/site";
import Reveal from "@/components/Reveal";
import { useT } from "@/i18n/LanguageContext";

const KEYS = ["emergency", "puncture", "newtyres", "parttyres", "runflat", "vehicles"];
const ICONS = { emergency: Zap, puncture: Wrench, newtyres: Truck, parttyres: RotateCcw, runflat: Disc3, vehicles: Car };
const IMAGES = { emergency: SERVICE_IMAGES.emergency, puncture: SERVICE_IMAGES.puncture, newtyres: SERVICE_IMAGES.replacement };

export default function Services() {
  const { t } = useT();
  const services = KEYS.map((key) => ({
    key,
    title: t.services.cards[key].title,
    desc: t.services.cards[key].desc,
    icon: ICONS[key],
    image: IMAGES[key],
  }));

  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative bg-white py-16 sm:py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-20">
          <Reveal>
            <div className="overline text-[#1E63FF]">{t.services.overline}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] mt-4 max-w-2xl leading-[1.02]">
              {t.services.heading1}<br />
              <span className="text-neutral-400">{t.services.heading2}</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-neutral-600 max-w-md text-base leading-relaxed">
              {t.services.sub}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 lg:gap-6">
          <Reveal className="md:col-span-8 lg:col-span-7 flex" direction="up" delay={0}>
            <FeaturedCard service={services[0]} t={t} />
          </Reveal>

          <Reveal className="md:col-span-4 lg:col-span-5 flex" direction="up" delay={120}>
            <ImageCard service={services[1]} />
          </Reveal>
          <Reveal className="md:col-span-4 lg:col-span-5 flex" direction="up" delay={180}>
            <ImageCard service={services[2]} />
          </Reveal>

          <Reveal className="md:col-span-4 lg:col-span-4 flex" direction="up" delay={60}>
            <IconCard service={services[3]} t={t} />
          </Reveal>
          <Reveal className="md:col-span-4 lg:col-span-4 flex" direction="up" delay={140}>
            <IconCard service={services[4]} t={t} />
          </Reveal>
          <Reveal className="md:col-span-4 lg:col-span-4 flex" direction="up" delay={220}>
            <IconCard service={services[5]} t={t} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ service, t }) {
  const Icon = service.icon;
  return (
    <a
      href={telHref}
      data-testid={SERVICES.card(service.key)}
      className="service-card group relative w-full overflow-hidden rounded-2xl bg-[#0A0A0A] text-white min-h-[420px] flex border border-black/10"
    >
      <img
        src={service.image}
        alt={service.title}
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
      <div className="relative p-8 lg:p-12 flex flex-col justify-between w-full">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E63FF] text-white text-xs font-bold tracking-wider-2 uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {t.services.badge247}
          </span>
          <ArrowUpRight className="w-7 h-7 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
        </div>

        <div className="max-w-xl">
          <Icon className="w-9 h-9 mb-5" strokeWidth={1.5} />
          <h3 className="font-display font-semibold text-2xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            {service.title}
          </h3>
          <p className="mt-4 text-white/75 text-base leading-relaxed max-w-md">
            {service.desc}
          </p>
          <div className="mt-7 inline-flex items-center gap-2 text-sm font-semibold border-b border-white/40 pb-1">
            {t.services.cta}
          </div>
        </div>
      </div>
    </a>
  );
}

function ImageCard({ service }) {
  const Icon = service.icon;
  return (
    <div
      data-testid={SERVICES.card(service.key)}
      className="service-card group relative w-full rounded-2xl overflow-hidden bg-white border border-black/10 min-h-[420px]"
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      </div>
      <div className="relative p-7 lg:p-9 h-full flex flex-col justify-end text-white">
        <Icon className="w-8 h-8 mb-4" strokeWidth={1.5} />
        <h3 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight leading-tight">
          {service.title}
        </h3>
        <p className="mt-3 text-white/80 text-sm leading-relaxed max-w-sm">
          {service.desc}
        </p>
      </div>
    </div>
  );
}

function IconCard({ service, t }) {
  const Icon = service.icon;
  return (
    <div
      data-testid={SERVICES.card(service.key)}
      className="service-card group w-full p-7 lg:p-8 rounded-2xl bg-[#F5F5F7] border border-black/5 min-h-[240px] flex flex-col"
    >
      <div className="w-12 h-12 rounded-lg bg-[#0A0A0A] text-white flex items-center justify-center group-hover:bg-[#1E63FF] transition-colors">
        <Icon className="w-6 h-6" strokeWidth={1.6} />
      </div>
      <h3 className="font-display font-semibold text-lg sm:text-2xl tracking-tight text-[#0A0A0A] mt-5 leading-tight">
        {service.title}
      </h3>
      <p className="mt-3 text-neutral-600 text-sm leading-relaxed">
        {service.desc}
      </p>
      <div className="mt-auto pt-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider-2 text-[#1E63FF]">
        <span>{t.services.available}</span>
        <ArrowUpRight className="w-3.5 h-3.5" />
      </div>
    </div>
  );
}
